
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { COMPANY_INFO, SERVICES, ASSETS } from '../data';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Forside', href: '/' },
    { name: 'Vores Projekter', href: '/projekter' },
    { name: 'Om os', href: '/om-os' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-slate-900/95 backdrop-blur-md py-3 shadow-lg' 
          : 'bg-linear-to-b from-slate-900/80 via-slate-900/40 to-transparent py-8'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <img 
              src={ASSETS.logo} 
              alt={COMPANY_INFO.name} 
              className={`transition-all duration-300 transform group-hover:scale-105 ${isScrolled ? 'h-20' : 'h-32'}`} 
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/' ? 'text-amber-500' : 'text-white/80 hover:text-white'
              }`}
            >
              Forside
            </Link>

            {/* Dropdown for Projekter & Ydelser */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <Link
                to="/projekter"
                className={`text-sm font-medium transition-colors flex items-center space-x-1 ${
                  location.pathname === '/projekter' || location.pathname.startsWith('/service/') ? 'text-amber-500' : 'text-white/80 hover:text-white'
                }`}
              >
                <span>Vores projekter</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </Link>

              {/* Dropdown Menu */}
              <div className={`absolute top-full left-0 w-64 pt-4 transition-all duration-300 ${isDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                <div className="bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden py-2">
                  {SERVICES.map((service) => {
                    const isActive = location.pathname === `/service/${service.slug}`;
                    return (
                      <Link
                        key={service.id}
                        to={`/service/${service.slug}`}
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          isActive 
                            ? 'text-amber-500 bg-white/5 font-medium' 
                            : 'text-white/70 hover:text-amber-500 hover:bg-white/5'
                        }`}
                      >
                        {service.title}
                      </Link>
                    );
                  })}
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <Link
                      to="/projekter"
                      className={`block px-4 py-2.5 text-sm font-semibold transition-colors ${
                        location.pathname === '/projekter'
                          ? 'text-amber-500 bg-white/5'
                          : 'text-white hover:text-amber-500 hover:bg-white/5'
                      }`}
                    >
                      Alle projekter
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/om-os"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/om-os' ? 'text-amber-500' : 'text-white/80 hover:text-white'
              }`}
            >
              Om os
            </Link>
            <Link
              to="/kontakt"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/kontakt' ? 'text-amber-500' : 'text-white/80 hover:text-white'
              }`}
            >
              Kontakt
            </Link>
            <div className="flex items-center space-x-4 pl-4 border-l border-white/10">
              <a href={`tel:${COMPANY_INFO.phone.simon.replace(/\s/g, '')}`} className="text-white hover:text-amber-500 transition-colors">
                <Phone size={18} />
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-2"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-t border-white/10 py-6 px-4 space-y-4 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setIsOpen(false)}
            className="block text-lg text-white font-medium"
          >
            Forside
          </Link>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between w-full">
              <Link
                to="/projekter"
                onClick={() => setIsOpen(false)}
                className="text-lg text-white font-medium"
              >
                Vores projekter
              </Link>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="p-2 text-white"
                aria-label="Toggle dropdown"
              >
                <ChevronDown size={20} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {isDropdownOpen && (
              <div className="pl-4 space-y-3 border-l border-white/10 mt-2">
                {SERVICES.map((service) => {
                  const isActive = location.pathname === `/service/${service.slug}`;
                  return (
                    <Link
                      key={service.id}
                      to={`/service/${service.slug}`}
                      onClick={() => setIsOpen(false)}
                      className={`block text-base transition-colors ${
                        isActive ? 'text-amber-500 font-medium' : 'text-white/60 hover:text-amber-500'
                      }`}
                    >
                      {service.title}
                    </Link>
                  );
                })}
                <Link
                  to="/projekter"
                  onClick={() => setIsOpen(false)}
                  className={`block text-base font-semibold transition-colors ${
                    location.pathname === '/projekter' ? 'text-amber-500' : 'text-white/80 hover:text-amber-500'
                  }`}
                >
                  Alle projekter
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/om-os"
            onClick={() => setIsOpen(false)}
            className="block text-lg text-white font-medium"
          >
            Om os
          </Link>
          <Link
            to="/kontakt"
            onClick={() => setIsOpen(false)}
            className="block text-lg text-white font-medium"
          >
            Kontakt
          </Link>
          <div className="pt-4 flex flex-col space-y-4 border-t border-white/10">
            <a href={`tel:${COMPANY_INFO.phone.simon}`} className="flex items-center text-white space-x-3">
              <Phone size={20} className="text-amber-500" />
              <span>{COMPANY_INFO.phone.simon} (Simon)</span>
            </a>
            <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center text-white space-x-3">
              <Mail size={20} className="text-amber-500" />
              <span>{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
